/**
 * Окошко «Новая комната»: ширина и длина в чистоте, от стены до стены, и сразу площадь.
 *
 * По «Создать» размер запоминается (`roomSize`), включается инструмент «Комната» — и
 * призрак комнаты этого размера едет за курсором, клик по плану ставит её (`usePlaceRoom`).
 * Стены — выбранного в панели типа и толщины.
 */
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { formatArea } from "@/shared/lib/geometry";
import { Tool, useEditorStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";

const MIN_SIDE = 500;
const MAX_SIDE = 30000;

const sideSchema = z
	.number()
	.min(MIN_SIDE, `Не меньше ${MIN_SIDE} мм`)
	.max(MAX_SIDE, `Не больше ${MAX_SIDE} мм`);

const roomSchema = z.object({ width: sideSchema, depth: sideSchema });

interface Props {
	onClose: () => void;
}

export const CreateRoomDialog = ({ onClose }: Props) => {
	const roomSize = useEditorStore((s) => s.roomSize);
	const form = useForm({
		defaultValues: { width: roomSize.width, depth: roomSize.depth },
		validators: { onChange: roomSchema },
		onSubmit: ({ value }) => {
			const editor = useEditorStore.getState();
			editor.setRoomSize(value);
			editor.setSelection(null);
			editor.setTool(Tool.Room);
			onClose();
		},
	});

	return (
		<UIKit.ConfirmModal
			title="Новая комната"
			description="Размеры — в чистоте, от стены до стены: стены встанут снаружи, и площадь будет ровно ширина × длина. После «Создать» комната поедет за курсором — кликните, куда её поставить."
			onClose={onClose}
		>
			<form className="mt-4 flex flex-col gap-3" action={() => form.handleSubmit()}>
				<UIKit.FormField form={form} name="width">
					{(field) => (
						<UIKit.NumberField
							label="Ширина"
							value={field.state.value}
							min={MIN_SIDE}
							max={MAX_SIDE}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<UIKit.FormField form={form} name="depth">
					{(field) => (
						<UIKit.NumberField
							label="Длина"
							value={field.state.value}
							min={MIN_SIDE}
							max={MAX_SIDE}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<form.Subscribe selector={(state) => state.values}>
					{({ width, depth }) => (
						<UIKit.Text className="text-sm text-ink">
							Площадь: <b className="text-ink">{formatArea(width * depth)}</b>
						</UIKit.Text>
					)}
				</form.Subscribe>
				<div className="mt-2 flex justify-end gap-2">
					<UIKit.Button type="button" className="px-3 text-sm" onClick={onClose}>
						Отмена
					</UIKit.Button>
					<UIKit.Button
						type="submit"
						className="border-accent bg-accent px-3 text-sm font-medium text-white hover:border-blue-700 hover:bg-blue-700"
					>
						Создать
					</UIKit.Button>
				</div>
			</form>
		</UIKit.ConfirmModal>
	);
};
